import React, { useContext, useState } from 'react';
import { Trans, withI18n } from '@lingui/react';
import { Form, Formik } from 'formik';
import styled from 'styled-components';
import { useRouter } from 'next/router';
import * as Yup from 'yup';
import axios from 'axios';
import { getUTMParams } from '~/components/UTMParams';
import ClassicInput from '~/components/inputs/ClassicInput';
import PhoneInput from '~/components/inputs/PhoneInput';
import { YellowButton } from '~/styles/buttons';
import { LangContext } from '~/providers/LangProvider';
import LoadingSplash from '~/components/loadingSplash/LoadingSplash';
import AlertMessage, { AlertType } from '~/components/AlertMessage.tsx';
import {t} from "@lingui/macro";

const getValidation = (i18n = null) => {
    return Yup.object().shape({
        name: Yup.string().required(i18n?._('contactForm.name.required')),
        email: Yup.string()
            .required(i18n?._('contactForm.email.required'))
            .email(i18n?._('contactForm.email.notValid')),
        message: Yup.string().required(i18n?._('contactForm.message.required')),
    });
};

/**
 * convert json data to form data for google API
 *
 * @param data
 * @returns {string}
 */
export function encodeData(data) {
    return Object.keys(data)
        .map(k => {
            return encodeURIComponent(k) + '=' + encodeURIComponent(data[k]);
        })
        .join('&');
}

/**
 * send request to Google Action Script API
 *
 * @param url
 * @param data
 * @param conversionName
 * @returns {{working: boolean, sent: boolean}}
 */
export async function sendGoogleScript(url, data, conversionName) {

    const output = {
        sent: false,
        error: false,
        working: true
    }

    const options = {
        method: 'POST',
        headers: { 'content-type': 'application/x-www-form-urlencoded' },
        data: encodeData(data),
        url,
    };

    try {
        const res = await axios(options);
        output.sent = true;

        if (typeof window !== 'undefined') {
            let dataLayer = window.dataLayer || [];
            dataLayer.push({ lead: conversionName });
        }

    } catch (err) {
        console.error('ContactForm - bad response from Google API!', err);
        output.error = true;
    } finally {
        output.working = false
    }

    return output
}

const ContactForm = ({description, googleScript, i18n}) => {

    const router = useRouter();
    const ctxLang = useContext(LangContext);
    const [startValidationOnChange, setStartValidationOnChange] = useState(
        false
    );

    const googleSheetName = 'responses';
    const [loading, setLoading] = useState(false);
    const [errorMessage, setErrorMessage] = useState(null);
    const [formSent, isFormSent] = useState(false);
    const [phoneValid, setPhoneValid] = useState(false);

    const handleSubmit = async (values) => {
        setLoading(true);

        const formData = {
            name: values.name,
            email: values.email,
            phone: values.phone.inputNumber,
            message: values.message,
            language: ctxLang.state.lang,
            country: ctxLang.state.country,
        };

        let formDataNameOrder = Object.assign({}, formData, getUTMParams(), {
            URL: `https://trisbee.com/${ctxLang.state.lang}${router.pathname}`,
        });

        //
        // merge data with google script config fields
        //
        const dataToSend = Object.assign({}, formDataNameOrder, {
            formGoogleSheetName: googleSheetName,
            formGoogleSendEmail: 'podpora@trisbee.com',
            formDataNameOrder: JSON.stringify(formDataNameOrder),
        });

        const response = await sendGoogleScript(
            googleScript,
            dataToSend,
            'successSendContactForm'
        );



        response.sent && history.pushState(null, '', `${window.location.pathname}?form_sent=true&form_name=contact_form_general`)
        isFormSent(response.sent);
        setLoading(response.loading);
        setErrorMessage(response.error);
    };

    if(formSent) {
        return (
            <ThankYouWrapper>
                <ThankYouHeading>
                    <Trans id="global.contact.thank_you.heading" />
                </ThankYouHeading>
                <ThankYouDescription>
                    <Trans id="global.contact.thank_you2.description" />
                </ThankYouDescription>
                <ThankYouCTA
                    onClick={() => isFormSent(false)}
                >
                    <Trans id="global.contact.thank_you.cta" />
                </ThankYouCTA>
            </ThankYouWrapper>
        );
    }

    return (
        <TopWrapper>
            <LoadingSplash
                isLoading={loading}
                overlay={false}
            >
                <FormWrapper>
                    {errorMessage && (
                        <AlertMessage type={AlertType.danger}>
                            <strong>
                                <Trans
                                    id={"component.contact_form.api_error"}
                                    message={"Something went wrong, please try again"}
                                />
                            </strong>
                        </AlertMessage>
                    )}
                    <Formik
                        validateOnChange={
                            startValidationOnChange
                        }
                        validateOnBlur={
                            startValidationOnChange
                        }
                        validationSchema={getValidation(
                            i18n
                        )}
                        initialValues={{}}
                        onSubmit={values => {
                            handleSubmit(values);
                        }}
                        validate={values => {
                            setStartValidationOnChange(
                                true
                            );

                            let errors = {};
                            if (!phoneValid) {
                                errors.phone = i18n._(
                                    'contactForm.phone.required'
                                );
                            }

                            return errors;
                        }}
                    >
                        {({ errors, touched }) => (
                            <FormikForm>
                                <ClassicInput
                                    name={'name'}
                                    touched={touched}
                                    errors={errors}
                                    label={i18n._(
                                        'contactForm.name.label'
                                    )}
                                />

                                <ClassicInput
                                    name={'email'}
                                    touched={touched}
                                    errors={errors}
                                    label={i18n._(
                                        'contactForm.email.label'
                                    )}
                                />

                                <PhoneInput
                                    name={'phone'}
                                    touched={touched}
                                    errors={errors}
                                    setPhoneValid={
                                        setPhoneValid
                                    }
                                    phoneErrorMessage={i18n._(
                                        'contactForm.phone.required'
                                    )}
                                    label={i18n._(
                                        'contactForm.phone.label'
                                    )}
                                />

                                <ClassicInput
                                    textAreaInput={true}
                                    name={'message'}
                                    touched={touched}
                                    errors={errors}
                                    label={i18n._(description ?? t`contactForm.message.label`)}
                                />

                                <CustomSubmit type="submit">
                                    <Trans id="global.cta.submit" />
                                </CustomSubmit>
                            </FormikForm>
                        )}
                    </Formik>
                </FormWrapper>
            </LoadingSplash>
        </TopWrapper>
    )
}

const TopWrapper = styled.div`
  padding-bottom: 100px;
`

const FormWrapper = styled.div`
  padding: 1px 15px 15px 15px;
`;

const ThankYouWrapper = styled.div`
    background: #ffffff;
    box-shadow: 0px 0px 33px #e9edec;
    border-radius: 8px;
    padding: 35px;
    display: flex;
    flex-direction: column;
    max-width: 500px;
    margin-bottom: auto;
`;

const ThankYouHeading = styled.div`
    font-style: normal;
    font-weight: bold;
    font-size: 35px;
    line-height: 56px;
    text-align: center;
    letter-spacing: -0.25px;
    color: #0cc8a8;
    margin-bottom: 20px;
`;

const ThankYouDescription = styled.div`
    font-style: normal;
    font-weight: normal;
    font-size: 16px;
    line-height: 25px;
    text-align: center;
    color: #3c3b3b;
    margin-bottom: 20px;
`;


const FormikForm = styled(Form)`
    width: 100%;
`;

const ThankYouCTA = styled(YellowButton)`
    width: 100%;
`;

const CustomSubmit = styled(YellowButton)`
  width: 100%;
  margin-top: 10px;
`

export default withI18n()(ContactForm)
